import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import api from './api';

export function useCredits(user) {
  const [credits, setCredits] = useState({ balance: 0, unlimited: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCredits = async () => {
      if (!user) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          if (isMounted) setLoading(false);
          return;
        }

        const response = await api.get('/api/credits/balance', {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        });
        
        if (isMounted) {
          setCredits(response.data);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to fetch credits:', err);
        if (isMounted) {
          setError(err);
          setCredits({ balance: 0, unlimited: false, error: true });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCredits();

    if (!user) return;

    // Subscribe to credits changes
    const channel = supabase
      .channel('credits-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credits',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchCredits();
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // Only depend on user.id, not the whole user object

  const refetchCredits = async () => {
    if (!user) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/credits/balance', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      setCredits(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
      setError(err);
      setCredits({ balance: 0, unlimited: false, error: true });
    }
  };

  return { credits, loading, error, refetch: refetchCredits };
}

